import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizarTexto } from '../utils/sanitizarTexto';

const FORGOT_URL = `${process.env.REACT_APP_API_URL}/auth/forgot-password`;
const FORGOT_STATUS_URL = `${process.env.REACT_APP_API_URL}/auth/forgot-password-status`;

const COOLDOWN_MESSAGE = 'Te enviamos un correo de recuperación, lo recibirás en tu bandeja de entrada en los próximos 5 minutos. Por favor, esperá antes de solicitar uno nuevo.';

export function ForgotPassword() {

    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const statusReqRef = useRef(null);
    const emailInputRef = useRef(null);

    const normalizeEmail = (v = '') => (v || '').trim().toLowerCase();

    const isValidEmail = (v = '') => {
        const s = (v || '').trim();
        if (!s) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    };

    const formatMMSS = (totalSeconds = 0) => {
        const s = Math.max(0, Number(totalSeconds) || 0);
        const mm = Math.floor(s / 60);
        const ss = s % 60;
        const mmStr = String(mm).padStart(2, '0');
        const ssStr = String(ss).padStart(2, '0');
        return `${mmStr}:${ssStr}`;
    };

    useEffect(() => {
        const syncFromDom = () => {
            const domValue = emailInputRef.current?.value || '';
            if (domValue && domValue !== email) {
                setEmail(domValue);
            }
        };

        const t1 = setTimeout(syncFromDom, 0);
        const t2 = setTimeout(syncFromDom, 250);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };

    }, []);

    useEffect(() => {
        if (cooldownSeconds <= 0) return;

        const intervalId = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) return 0;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [cooldownSeconds]);

    useEffect(() => {
        if (cooldownSeconds === 0 && message === COOLDOWN_MESSAGE) {
            setMessage(null);
        }
    }, [cooldownSeconds, message]);

    useEffect(() => {
        const emailNorm = normalizeEmail(email);
        if (!emailNorm || !isValidEmail(emailNorm)) {
            setCooldownSeconds(0);
            setCheckingStatus(false);

            if (message === COOLDOWN_MESSAGE) setMessage(null);

            if (statusReqRef.current) {
                statusReqRef.current.abort();
                statusReqRef.current = null;
            }

            return;
        }


        const timeoutId = setTimeout(async () => {
            setCheckingStatus(true);

            try {

                if (statusReqRef.current) statusReqRef.current.abort();

                const controller = new AbortController();
                statusReqRef.current = controller;

                const res = await axios.get(
                    FORGOT_STATUS_URL,
                    {
                        params: { email: emailNorm },
                        signal: controller.signal
                    }
                );

                const remaining = Number(res?.data?.cooldown_seconds || 0);

                if (remaining > 0) {
                    setCooldownSeconds(remaining);

                    setMessage(COOLDOWN_MESSAGE);
                    setError(null);
                } else {

                    if (message === COOLDOWN_MESSAGE) setMessage(null);
                    setCooldownSeconds(0);
                }

            } catch (err) {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;

                setCooldownSeconds(0);
                if (message === COOLDOWN_MESSAGE) setMessage(null);

            } finally {
                statusReqRef.current = null;
                setCheckingStatus(false);
            }
        }, 450);

        return () => clearTimeout(timeoutId);
    }, [email]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const emailNorm = normalizeEmail(email);

        if (sending || checkingStatus || cooldownSeconds > 0) return;

        setSending(true);
        setMessage(null);
        setError(null);

        try {
            const res = await axios.post(
                FORGOT_URL,
                JSON.stringify({ email: emailNorm }),
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            const msg = res?.data?.message
                || 'Si el correo existe en el sistema, te enviamos un email con instrucciones para recuperar tu contraseña.';

            const remaining = Number(res?.data?.cooldown_seconds || 0);

            setMessage(msg);


            if (remaining > 0) {
                setCooldownSeconds(remaining);

                if ((msg || '').includes('Ya te enviamos un correo de recuperación recientemente')) {
                    setMessage(COOLDOWN_MESSAGE);
                }
            }

        } catch (err) {
            const msg = err?.response?.data?.message || 'Ocurrió un error al procesar la solicitud.';
            const remaining = Number(err?.response?.data?.cooldown_seconds || 0);

            if (remaining > 0) {
                setCooldownSeconds(remaining);
                setMessage(COOLDOWN_MESSAGE);
                setError(null);
            } else {
                setError(msg);
            }

        } finally {
            setSending(false);
        }
    };

    const handleVolver = (e) => {
        e.preventDefault();
        navigate('/login');
    };

    const showCooldownBlock = cooldownSeconds > 0 && isValidEmail(email);
    const showButton = !showCooldownBlock && !checkingStatus;

    const isButtonDisabled = sending || checkingStatus || !email;

    const cooldownSuffix = showCooldownBlock ? ` (${formatMMSS(cooldownSeconds)})` : '';

    return (
        <div className="body-login">
            <div className="row header">
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
                <div className="col-md-6 col-sm-12 p-0 logo-container">
                    <img className="logo-cortado" src="/Logo-Ethical.png" />
                </div>
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
            </div>

            <div className="row body">
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
                <div className="col-md-6 col-sm-12 body-content">
                    <p className="title">Recuperar contraseña</p>

                    <p className="text-muted" style={{ fontSize: '14px' }}>
                        Ingresá tu correo electrónico y, si existe en el sistema, te enviaremos una contraseña temporal y un enlace para restablecer tu contraseña.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <p className="label">
                                <span><img src="message.png" /></span>
                                <span className="label-text">Correo electrónico</span>
                            </p>
                            <p>
                                <input
                                    ref={emailInputRef}
                                    className="form-text"
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    onChange={(e) => {
                                        setEmail(sanitizarTexto(e.target.value));
                                        setError(null);

                                        if (message && message !== COOLDOWN_MESSAGE) {
                                            setMessage(null);
                                        }
                                    }}
                                    value={email}
                                    required
                                />
                            </p>
                        </div>


                        {showCooldownBlock && (
                            <p style={{ marginTop: '10px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                                {COOLDOWN_MESSAGE}{cooldownSuffix}
                            </p>
                        )}

                        {showButton && (
                            <div className="form-group-actions">
                                <button className="btn-primary" 
                                
                                 style={{
                                    backgroundColor: '#0A9E95',
                                    borderColor: '#0A9E95',
                                    color: '#ffffff',
                                    marginTop: '10px'
                                }}

                                type="submit" disabled={isButtonDisabled}>
                                    <p>{sending ? 'Enviando...' : 'Enviar instrucciones'}</p>
                                </button>
                                <button
                                    className="btn-primary"
                                    type="button"
                                    onClick={handleVolver}
                                    disabled={sending || checkingStatus}
                                    style={{backgroundColor: '#666666',borderColor: '#666666',color: '#ffffff', marginTop: '10px' }}
                                >
                                    <p>Volver</p>
                                </button>
                            </div>
                        )}
                    </form>


                    {message && !showCooldownBlock && (
                        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                            {message}
                        </p>
                    )}

                    {error && !showCooldownBlock && (
                        <p style={{ marginTop: '20px', fontSize: '14px', color: 'red', textAlign: 'center' }}>
                            {error}
                        </p>
                    )}
                </div>
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
            </div>
        </div>
    );
}